import type { MissionControlEnvironment, ClaimDevBoxRequest, ExtendResult } from '../types/environment';
import { TENANT_ID } from '../types/environment';

const MISSION_CONTROL_BASE = 'https://missioncontrol.services.talxis.com';

function envBasePath(tenantId: string = TENANT_ID): string {
  return `${MISSION_CONTROL_BASE}/resources/tenants/${tenantId}/environments`;
}

/** Fetch all environments visible to the user */
export async function fetchEnvironments(upn: string, tenantId: string = TENANT_ID): Promise<MissionControlEnvironment[]> {
  const res = await fetch(`${envBasePath(tenantId)}?upn=${encodeURIComponent(upn)}`);
  if (!res.ok) throw new Error(`Failed to fetch environments: ${res.status}`);
  const data: { value: MissionControlEnvironment[] } = await res.json();
  return data.value;
}

/** Fetch environments for a specific workspace */
export async function fetchEnvironmentsByWorkspace(
  workspaceCode: string,
  upn: string,
  tenantId: string = TENANT_ID,
): Promise<MissionControlEnvironment[]> {
  const all = await fetchEnvironments(upn, tenantId);
  return all.filter((env) => env.WorkspaceCode === workspaceCode);
}

/** Claim a new DevBox environment */
export async function claimDevBox(req: ClaimDevBoxRequest, tenantId: string = TENANT_ID): Promise<void> {
  const res = await fetch(`${envBasePath(tenantId)}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Failed to claim DevBox: ${res.status}${text ? ` — ${text}` : ''}`);
  }
}

/** Extend a DevBox expiration (1–10 days, max 2 extensions) */
export async function extendDevBox(envId: string, days: number, tenantId: string = TENANT_ID): Promise<ExtendResult> {
  const res = await fetch(`${envBasePath(tenantId)}/${envId}/extendexpiration`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ days }),
  });
  if (!res.ok) throw new Error(`Failed to extend DevBox: ${res.status}`);
  return res.json();
}

/** Share a DevBox with another user */
export async function shareDevBox(envId: string, upn: string, tenantId: string = TENANT_ID): Promise<void> {
  const res = await fetch(`${envBasePath(tenantId)}/${envId}/adduser`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ upn }),
  });
  if (!res.ok) throw new Error(`Failed to share DevBox: ${res.status}`);
}

/** Delete a DevBox environment */
export async function deleteDevBox(envId: string, tenantId: string = TENANT_ID): Promise<void> {
  const res = await fetch(`${envBasePath(tenantId)}/${envId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete DevBox: ${res.status}`);
}
